import { Badge, Box, CircularProgress, Tab, Tabs } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import flagSrc from '../../Assets/Images/Flag.gif'
import CONSTANTS from '../../Constants'
import { updateNotificationsIsRead } from '../../Helpers/NotificationHelper'
import { notificationPageUrl } from '../../Helpers/Util'
import { useAppDispatch, useAppSelector } from '../../Store/Hooks'
import { showAlert } from '../../Store/Slice/alert.slice'
import { setLoader } from '../../Store/Slice/loader.slice'
import {
  clearNotificationsList,
  getNotifications,
  setUnreadNotifIds,
  updateNotificationsReadStatus,
} from '../../Store/Slice/notifications.slice'
import { FilterSearchWithList } from '../Shared/Filters/FilterSearchWithList'
import MarkAllRead from './MarkAllRead'
import Notification from './Notification'
import styles from './Notification.module.scss'

const NotificationListing = forwardRef((props: any, ref: any) => {
  const { onClickNotification, isPopup = false } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { notificationsList, popupNotificationsList, unReadNotifIds } = useAppSelector(
    (state) => state.notifications,
  )
  const { myUnreadCount, allUnreadCount, notifications, count } = isPopup
    ? popupNotificationsList
    : notificationsList

  const containerRef = useRef<HTMLDivElement>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedFilter, setSelectedFilter] = useState<{
    page: number
    limit: number
    selectedTab: string
    filter: {
      eventType: any[]
      createdAt?: {
        filterBy: string
        filterValue: string
        filterCondition: string
        filterValueTo?: string
      }
      statusType: any[]
    }
  }>({
    page: 1,
    limit: 20,
    selectedTab: CONSTANTS.NOTIFICATION_TAB_VALUE[0].value,
    filter: {
      eventType: CONSTANTS.NOTIFICATION_EVENT_FILTER_LIST,
      statusType: CONSTANTS.NOTIFICATION_READ_STATUS_FILTER_LIST,
    },
  })
  const hasMore = selectedFilter.page * selectedFilter.limit < count

  const fetchNotifications = async () => {
    if (isLoading) {
      return
    }
    setIsLoading(true)

    const notificationFilter: Array<{
      filterBy: string
      filterCondition: string
      filterValue: any[] | string | boolean
      filterValueTo?: string
    }> = []
    ;(['eventType', 'statusType'] as Array<'eventType' | 'statusType'>).forEach((key: string) => {
      let value = selectedFilter.filter[key as 'eventType' | 'statusType']
        ?.filter((f: any) => f.select)
        .map((f: any) => f.value)
      if (value?.length) {
        if (key === 'statusType') {
          value = value.map((item: any) => {
            return item === 'READ'
          })
        }
        notificationFilter.push({
          filterBy: key === 'eventType' ? CONSTANTS.EVENT_KEY : CONSTANTS.READ_KEY,
          filterCondition: value.length > 1 ? 'in' : 'equals',
          filterValue: value.length > 1 ? value : value[0],
        })
      }
    })

    if (selectedFilter.filter.createdAt?.filterValue) {
      notificationFilter.push({
        filterBy: 'createdAt',
        filterCondition: selectedFilter.filter.createdAt.filterCondition,
        filterValue: selectedFilter.filter.createdAt.filterValue,
        ...(selectedFilter.filter.createdAt.filterCondition === 'between' && {
          filterValueTo: selectedFilter.filter.createdAt.filterValueTo,
        }),
      })
    }

    const payload = {
      notificationFilter,
      page: selectedFilter.page,
      limit: selectedFilter.limit,
      myNotification: selectedFilter.selectedTab === CONSTANTS.NOTIFICATION_TAB_VALUE[0].value,
    }

    await dispatch(getNotifications({ apiPayload: payload, showLoader: false, isPopup }) as any)
    setTimeout(() => {
      setIsLoading(false)
    }, 100)
  }

  // Handle scroll event
  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current
      if (scrollTop + clientHeight >= scrollHeight && hasMore && !isLoading) {
        setSelectedFilter((prev: any) => ({
          ...prev,
          page: prev.page + 1,
        }))
      }
    }
  }

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent | null, newValue: string) => {
    dispatch(clearNotificationsList({ isPopup }))
    setSelectedFilter((prev: any) => ({
      ...prev,
      selectedTab: newValue,
      page: 1, // Reset page
    }))
    if (containerRef.current) {
      containerRef.current.scrollTop = 0 // Scroll container to top
    }
  }

  const handleFilterChange = (newFilterList: any) => {
    setSelectedFilter((prev: any) => ({
      ...prev,
      page: 1, // Reset page when filters change
      filter: {
        ...prev.filter,
        ...newFilterList,
      },
    }))
    if (containerRef.current) {
      containerRef.current.scrollTop = 0 // Scroll container to top
    }
  }

  useEffect(() => {
    fetchNotifications()
  }, [selectedFilter])

  useEffect(() => {
    dispatch(clearNotificationsList({ isPopup }))
  }, [])

  useEffect(() => {
    dispatch(setUnreadNotifIds({ ids: notifications.filter((n) => !n.isRead).map((n) => n._id) }))
  }, [notifications])

  useImperativeHandle(ref, () => ({
    getNotifications: () => notifications,
  }))

  const handleRedirectNotification = async (notification: any) => {
    if (redirectionId !== notification?._id && !isRedirecting) {
      setIsRedirecting(true)
      redirectionId = notification?._id
      if (onClickNotification) {
        onClickNotification()
      }
      const pageUrl = await notificationPageUrl(notification, dispatch)
      if (pageUrl) {
        dispatch(setLoader(true))
        await updateNotificationsIsRead(
          dispatch,
          setUnreadNotifIds,
          updateNotificationsReadStatus,
          unReadNotifIds,
        )
        dispatch(setLoader(false))
        navigate(pageUrl)
      } else {
        dispatch(showAlert([true, 'Unable to redirect to the notification page', 'error']))
      }
      setIsRedirecting(false)
    }
  }

  const maxHeight = isPopup ? '60vh' : 'calc(100vh - 256px)'
  let redirectionId: null | string = null

  return (
    <div>
      {isPopup && (
        <div className={styles.header}>
          <p>{CONSTANTS.NOTIFICATIONS}</p>
          <MarkAllRead />
        </div>
      )}
      <Box className={styles.box}>
        <Tabs
          value={selectedFilter.selectedTab}
          onChange={handleTabChange}
          scrollButtons={false}
          allowScrollButtonsMobile
        >
          {CONSTANTS.NOTIFICATION_TAB_VALUE.map((tab) => {
            const unreadCount =
              tab.value === CONSTANTS.NOTIFICATION_TAB_VALUE[0].value
                ? myUnreadCount
                : allUnreadCount
            return (
              <Tab
                key={tab.value}
                className={styles.tab}
                value={tab.value}
                label={
                  <Badge
                    color='error'
                    variant={'dot'}
                    overlap='circular'
                    invisible={!unreadCount}
                    sx={selectedFilter.selectedTab !== tab.value ? { opacity: 0.7 } : {}}
                  >
                    {tab.label}
                  </Badge>
                }
              />
            )
          })}
        </Tabs>
        <div className={styles.filterWrapper}>
          <FilterSearchWithList
            search={CONSTANTS.SEARCH_BY_EVENT}
            title={CONSTANTS.FILTER_BY_EVENT}
            clear
            listItems={selectedFilter.filter.eventType}
            onUpdateListItems={(data) => handleFilterChange({ eventType: data })}
            isPopup={isPopup}
            showBorder={!isPopup}
          />
          {!isPopup && (
            <>
              <FilterSearchWithList
                dateTypeFilter={true}
                title={CONSTANTS.FILTER_BY_DATE}
                filterOptions={CONSTANTS.DATE_FILTER_OPTION}
                showBorder={true}
                listItems={[]}
                onUpdateListItems={() => void 0}
                filterConditionLabel={'Select'}
                onUpdateDate={(data: any) => {
                  handleFilterChange({ createdAt: data })
                }}
                clear={true}
                selectedDateFilter={
                  selectedFilter.filter?.createdAt?.filterValue
                    ? ([selectedFilter.filter.createdAt] as any[])
                    : []
                }
              />
              <FilterSearchWithList
                title={CONSTANTS.FILTER_BY_READ_STATUS}
                listItems={selectedFilter.filter.statusType}
                onUpdateListItems={(data) => handleFilterChange({ statusType: data })}
                showBorder={true}
                isMultiple={false}
              />
            </>
          )}
        </div>
      </Box>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{ maxHeight, minHeight: '100px', overflowY: 'auto' }}
      >
        <ul>
          {notifications?.map((notification: any) => (
            <li
              key={notification?._id}
              onClick={() => handleRedirectNotification(notification)}
              className={`${styles.notificationList}  cursor-pointer`}
            >
              <Notification
                title={notification?.notificationTitle}
                description={notification?.notificationMessage}
                isRead={notification?.isRead}
                datetime={notification?.createdAt}
              />
            </li>
          ))}
        </ul>
        {!hasMore && !isLoading && (
          <div className={styles.footerContainer}>
            <div className={styles.img}>
              <img src={flagSrc} alt='Flag gif' />
            </div>
            <p className={styles.text}>{CONSTANTS.NOTIFICATION_INFO}</p>
          </div>
        )}
        {isLoading && (
          <div className='d-flex justify-content-center mt-2'>
            <CircularProgress variant='indeterminate' />
          </div>
        )}
      </div>
    </div>
  )
})

NotificationListing.displayName = 'NotificationListing'
export default NotificationListing
